/* Prepare large single/multi photos before the cart request, with traceable output. */
(() => {
  const order = globalThis.HeirloomReliefOrder;
  const decoderUrl = document.querySelector('[data-memory-photo-preparation]')?.dataset.heicDecoderUrl;
  let queue = Promise.resolve(), decoderPromise;
  const loadDecoder = () => {
    if (typeof globalThis.heic2any === 'function') return Promise.resolve();
    if (!decoderPromise) decoderPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = decoderUrl;
      script.onload = () => typeof globalThis.heic2any === 'function' ? resolve() : reject(new Error('HEIC conversion is unavailable. Try a JPG or PNG.'));
      script.onerror = () => { script.remove(); reject(new Error('HEIC conversion could not load. Please try again.')); };
      document.head.appendChild(script);
    }).catch(error => { decoderPromise = null; throw error; });
    return decoderPromise;
  };
  const decode = async blob => {
    const url = URL.createObjectURL(blob), image = new Image();
    try { image.src = url; await image.decode(); return image; }
    finally { URL.revokeObjectURL(url); }
  };
  const processPhoto = async (file, isCurrent) => {
    const checkCurrent = () => { if (!isCurrent()) throw new Error('Photo selection changed.'); };
    checkCurrent();
    const type = await order.photoType(file);
    const input = { filename: file.name, bytes: file.size, mimeType: type.mime, sha256: await order.sha256(file) };
    checkCurrent();
    if (file.size <= order.maxPreparedPhotoBytes) return { file, input, changed: false };
    let image;
    try { image = await decode(file); }
    catch {
      if (type.mime !== 'image/heic') throw new Error('This photo could not be opened. Choose another image.');
      await loadDecoder();
      checkCurrent();
      let converted;
      try { converted = await globalThis.heic2any({ blob: file, toType: 'image/jpeg', quality: 0.95 }); }
      catch { throw new Error('This HEIC photo could not be prepared. Try a JPG or PNG.'); }
      if (Array.isArray(converted)) {
        if (converted.length !== 1) throw new Error('Choose a HEIC file containing a single photo, or use JPG.');
        converted = converted[0];
      }
      image = await decode(converted);
    }
    checkCurrent();
    const canvas = document.createElement('canvas');
    try {
      // Start with full detail; reduce dimensions only if high-quality encoding is still too large.
      for (const maxEdge of [4096, 3500, 3000, 2500, 2000]) {
        const scale = Math.min(1, maxEdge / Math.max(image.naturalWidth, image.naturalHeight));
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Photo preparation is unavailable. Please try again.');
        context.fillStyle = '#fff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        for (const quality of [0.94, 0.9, 0.86]) {
          checkCurrent();
          const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));
          checkCurrent();
          if (!blob) throw new Error('This photo could not be prepared. Please choose it again.');
          if (blob.size <= order.maxPreparedPhotoBytes) {
            return {
              file: new File([blob], `${file.name.replace(/\.[^.]+$/, '')}.jpg`, { type: 'image/jpeg', lastModified: file.lastModified }),
              input, changed: true, width: canvas.width, height: canvas.height, quality,
            };
          }
        }
      }
      throw new Error('This photo is still too large after preparation. Choose a smaller JPG or PNG.');
    } finally { canvas.width = canvas.height = 0; }
  };
  const prepare = (file, isCurrent = () => true) => {
    // Serial decoding avoids holding three large canvases on a phone at once.
    const task = queue.then(() => processPhoto(file, isCurrent));
    queue = task.catch(() => {});
    return task;
  };
  globalThis.HeirloomMemoryPhotos = { prepare };
})();
